import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const AITaskContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFIED_STORAGE_KEY = 'beatlab52_notified_tasks';

function getNotifiedSet() {
  try {
    const stored = localStorage.getItem(NOTIFIED_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function markNotified(taskKey) {
  const set = getNotifiedSet();
  set.add(taskKey);
  const arr = Array.from(set);
  if (arr.length > 200) arr.splice(0, arr.length - 200);
  localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify(arr));
}

function isAlreadyNotified(taskKey) {
  return getNotifiedSet().has(taskKey);
}

export function AITaskProvider({ children }) {
  const { token, isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [activeTasks, setActiveTasks] = useState([]);
  const pollRef = useRef(null);
  const hasProcessingRef = useRef(false);

  const fetchTasks = useCallback(async () => {
    if (!isAdmin || !token) return;
    try {
      const res = await axios.get(`${API}/ai/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      const newTasks = res.data || [];
      setTasks(prev => {
        if (prev.length !== newTasks.length) return newTasks;
        const changed = newTasks.some((task, i) =>
          prev[i]?.status !== task.status || prev[i]?.progress !== task.progress || prev[i]?.settings !== task.settings
        );
        return changed ? newTasks : prev;
      });
    } catch (error) {
    }
  }, [isAdmin, token]);

  useEffect(() => {
    if (!isAdmin || !token) {
      setTasks([]);
      setActiveTasks([]);
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    fetchTasks();

    const startPolling = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      const interval = hasProcessingRef.current ? 5000 : 30000;
      pollRef.current = setInterval(fetchTasks, interval);
    };

    startPolling();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAdmin, token, fetchTasks]);

  useEffect(() => {
    const processing = tasks.filter(t =>
      t.status === 'processing' ||
      t.status === 'pending' ||
      t.status === 'in_queue'
    );
    setActiveTasks(processing);

    const wasProcessing = hasProcessingRef.current;
    hasProcessingRef.current = processing.length > 0;

    if (wasProcessing !== hasProcessingRef.current) {
      if (pollRef.current) clearInterval(pollRef.current);
      if (hasProcessingRef.current) {
        pollRef.current = setInterval(() => {
          if (typeof fetchTasks === 'function') fetchTasks();
        }, 5000);
      } else {
        pollRef.current = setInterval(() => {
          if (typeof fetchTasks === 'function') fetchTasks();
        }, 30000);
      }
    }

    const now = Date.now();
    const MAX_NOTIFICATION_AGE_MS = 10 * 60 * 1000;
    const completed = tasks.filter(t => t.status === 'completed' && (t.audio_url || t.result_url));
    completed.forEach(task => {
      const notifiedKey = `${task.id || task.task_id}`;
      if (!isAlreadyNotified(notifiedKey)) {
        const createdAt = task.created_at ? new Date(task.created_at).getTime() : 0;
        if (createdAt && (now - createdAt) > MAX_NOTIFICATION_AGE_MS) {
          markNotified(notifiedKey);
          return;
        }
        markNotified(notifiedKey);
        const title = task.title || task.prompt?.slice(0, 30) || 'AI Track';
        toast.success(`"${title}" is ready!`, {
          duration: 8000,
          action: {
            label: 'Open AI Studio',
            onClick: () => {
              window.location.href = '/admin/ai-studio';
            }
          }
        });
      }
    });
  }, [tasks, fetchTasks]);

  const refreshTasks = useCallback(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <AITaskContext.Provider value={{ tasks, activeTasks, refreshTasks }}>
      {children}
    </AITaskContext.Provider>
  );
}

export function useAITasks() {
  const ctx = useContext(AITaskContext);
  if (!ctx) return { tasks: [], activeTasks: [], refreshTasks: () => {} };
  return ctx;
}
